import React, { Fragment, lazy, Suspense, useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Checkbox from '@atlaskit/checkbox';
import { type OptionType, type SelectValue } from '@atlaskit/feedback-collector/types';
import { FlagGroup } from '@atlaskit/flag';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import InfoIcon from '@atlaskit/icon/core/status-information';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import AtlassianIntelligenceIcon from '../../atlassian-intelligence-icon';
import { OpenChatButton } from '../../common';
import {
	AiBrand,
	ATLASSIAN_INTELLIGENCE_TRUST_LINK,
	ATLASSIAN_ROVO_LINK,
	CardTemplate,
	FEEDBACK_CONTEXT_CF,
	FEEDBACK_ENTRYPOINT_ID_SAIN,
	SAC_ATLASSIAN_INTELLIGENCE_TRUST_LINK,
} from '../../constants';
import { messages } from '../../messages';
import { DefinitionFooter } from '../../v2/definitions/footer/definition-footer';
import { AIAnswerWhoFooter } from '../ai-answer-body/ai-answer-result';
import { useAIAnswerContext } from '../ai-answer-context';
import { NLPSearchResultFormat } from '../types';

import { FeedbackButtons } from './feedback-buttons';
import { FollowUpSuggestionFooter } from './followup-suggestion-footer';
import {
	AIAnswerContentBrandWrapper,
	AIAnswerFeedbackWrapper,
	AIAnswerFooterContentWarningLink,
	AIAnswerFooterInformationContentVary,
	AIAnswerFooterInformationContentVaryLinkText,
	AIAnswerFooterPoweredBy,
	AIAnswerFooterPoweredByAIWrapper,
	AIAnswerFooterPoweredByLink,
	AIAnswerFooterWrapper,
	AIAnswerReportDescriptionWrapper,
} from './styled';

const AkFeedbackCollector = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-collector" */ '@atlaskit/feedback-collector'
	).then((module) => {
		return {
			default: module.default,
		};
	}),
);

const AkFeedbackFlag = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-flag" */ '@atlaskit/feedback-collector'
	).then(({ FeedbackFlag }) => {
		return {
			default: FeedbackFlag,
		};
	}),
);

export const AIAnswerContentWarningAndBrandSection = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { brand, cardTemplate, sourceProduct, showGenericFooter } = useAIAnswerContext();
	if (showGenericFooter) {
		return (
			<AIAnswerContentBrandWrapper>
				<AIAnswerFooterInformationContentVary>
					<Lozenge>{formatMessage(messages.ai_answer_dialog_footer_beta_tag)}</Lozenge>
					<InfoIcon
						label=""
						color={token('color.icon.subtle', '#7A869A')}
						LEGACY_size="small"
						size="small"
					/>
					<AIAnswerFooterInformationContentVaryLinkText>
						<AIAnswerFooterContentWarningLink
							rel="noopener noreferrer"
							href={SAC_ATLASSIAN_INTELLIGENCE_TRUST_LINK}
							target="_blank"
						>
							{formatMessage(messages.ai_answer_dialog_footer_content_quality_short)}
						</AIAnswerFooterContentWarningLink>
					</AIAnswerFooterInformationContentVaryLinkText>
				</AIAnswerFooterInformationContentVary>
				<AIAnswerFooterPoweredBy>
					<AIAnswerFooterPoweredByAIWrapper>
						<AIAnswerFooterPoweredByLink
							rel="noopener noreferrer"
							href={SAC_ATLASSIAN_INTELLIGENCE_TRUST_LINK}
							target="_blank"
						>
							{formatMessage(messages.ai_answer_dialog_loaded_footer_sac)}
						</AIAnswerFooterPoweredByLink>
					</AIAnswerFooterPoweredByAIWrapper>
				</AIAnswerFooterPoweredBy>
			</AIAnswerContentBrandWrapper>
		);
	}
	return (
		<AIAnswerContentBrandWrapper>
			<AIAnswerFooterInformationContentVary>
				<InfoIcon
					label=""
					color={token('color.icon.subtle', '#626F86')}
					LEGACY_size="small"
					size="small"
				/>
				<AIAnswerFooterInformationContentVaryLinkText>
					<Link
						rel="noopener noreferrer"
						appearance="subtle"
						href={
							sourceProduct === 'sac'
								? SAC_ATLASSIAN_INTELLIGENCE_TRUST_LINK
								: ATLASSIAN_INTELLIGENCE_TRUST_LINK
						}
					>
						{formatMessage(
							cardTemplate === CardTemplate.RIGHT_PANEL
								? messages.ai_answer_dialog_footer_content_quality_short
								: messages.ai_answer_dialog_footer_content_quality_long,
						)}
					</Link>
				</AIAnswerFooterInformationContentVaryLinkText>
			</AIAnswerFooterInformationContentVary>
			<AIAnswerFooterPoweredBy>
				{brand === AiBrand.ROVO ? (
					<>
						<Link rel="noopener noreferrer" appearance="subtle" href={ATLASSIAN_ROVO_LINK}>
							Rovo
						</Link>
						<RovoIcon label="" color={token('color.icon.subtle')} spacing="spacious" />
					</>
				) : (
					sourceProduct !== 'sac' && (
						<AIAnswerFooterPoweredByAIWrapper>
							{formatMessage(messages.ai_answer_dialog_loaded_footer)}
							<AtlassianIntelligenceIcon greyed size={'large'} />
						</AIAnswerFooterPoweredByAIWrapper>
					)
				)}
			</AIAnswerFooterPoweredBy>
		</AIAnswerContentBrandWrapper>
	);
};

type SmartAnswerFooterSectionProps = {
	showOpenChat?: boolean;
	showGenericFooter?: boolean;
};

export const SmartAnswerFooterSection = ({
	showOpenChat,
}: SmartAnswerFooterSectionProps): React.JSX.Element => {
	const { commonAttributes } = useAIAnswerContext();
	const { query, answerString } = commonAttributes;

	return (
		<AIAnswerFooterWrapper>
			{showOpenChat && !fg('rovo_ai_mode_entrypoints_enabled') && (
				<OpenChatButton prompt={query} query={query} answerString={answerString} source={'sain'} />
			)}
			<FollowUpSuggestionFooter />
		</AIAnswerFooterWrapper>
	);
};

type AIAnswerBaseFooterProps = {
	showWhoAnswer?: boolean;
	showOpenChat?: boolean;
	showGenericFooter?: boolean;
};

const AIAnswerBaseFooter = ({ showWhoAnswer, showOpenChat }: AIAnswerBaseFooterProps) => {
	const { formatMessage, locale } = useIntl();
	const {
		commonAttributes,
		userDetails: { name, id },
		readingAidsOptions,
		loading,
		brand,
		isAnswered,
		showChatFollowUps,
		sourceProduct,
	} = useAIAnswerContext();
	const { errorState, answerFormat, followUps } = commonAttributes;

	const [showFeedbackModal, setFeedbackModal] = useState(false);
	const [displayFlag, setDisplayFlag] = useState(false);
	const [hasConsent, setConsent] = useState(false);
	const [topicsNotRelevant, setTopicsNotRelevant] = useState(false);

	const feedbackContextObject = {
		searchSessionId: commonAttributes.searchSessionId,
		source: commonAttributes.source,
		queryHash: commonAttributes.queryHash,
		cloudId: commonAttributes.cloudId,
		prompt: hasConsent ? commonAttributes.query : undefined,
		answer: hasConsent ? commonAttributes.answerString : undefined,
		documentId: readingAidsOptions?.contentARI,
		errorState: errorState,
		topicsNotRelevant: topicsNotRelevant ? true : undefined,
	};

	const feedbackContext = Object.entries(feedbackContextObject)
		.map(([key, value]) => `${key}: ${value}`)
		.join(', ');

	const toggleConsentCheckbox = useCallback(() => {
		setConsent(!hasConsent);
	}, [hasConsent]);
	const toggleTopicsNotRelevantCheckbox = useCallback(() => {
		setTopicsNotRelevant(!topicsNotRelevant);
	}, [topicsNotRelevant]);

	const topicsEnabled = fg('kd_topics_in_person_card');

	const brandName =
		brand === AiBrand.ROVO ? 'Rovo' : sourceProduct === 'sac' ? '' : 'Atlassian Intelligence';

	const renderOpenChatButton =
		showOpenChat && showChatFollowUps && !loading && isAnswered && fg('kd_smart_answers_open_chat');

	// Custom feedback options
	const SAC_CUSTOM_FEEDBACK_OPTIONS: OptionType[] = [
		{
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
			label: 'Wasn’t relevant',
			value: 'not_relevant' as SelectValue,
		},
		{
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
			label: 'Wasn’t accurate',
			value: 'not_accurate' as SelectValue,
		},
		{
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
			label: 'Too slow',
			value: 'too_slow' as SelectValue,
		},
		{
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
			label: 'Unhelpful links',
			value: 'unuseful_links' as SelectValue,
		},
		{
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
			label: 'Other',
			value: 'other' as SelectValue,
		},
	];

	const GENERIC_FEEDBACK_GROUPS_LABELS = {
		empty: {
			fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
			selectOptionLabel: formatMessage(
				messages.ai_answer_component_report_empty_option_share_label,
			),
		},
		question: {
			fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
			selectOptionLabel: formatMessage(messages.ai_answer_component_report_relevance_option_label),
		},
		comment: {
			fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
			selectOptionLabel: formatMessage(messages.ai_answer_component_report_inaccurate_option_label),
		},
		bug: {
			fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
			selectOptionLabel: formatMessage(messages.ai_answer_component_report_offensive_option_label),
		},
		suggestion: {
			fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
			selectOptionLabel: formatMessage(messages.ai_answer_component_report_other_option_label),
		},
	};

	return (
		<Fragment>
			{showWhoAnswer && answerFormat === NLPSearchResultFormat.JSON && <AIAnswerWhoFooter />}
			{!loading && (
				<AIAnswerFeedbackWrapper isCompact>
					<FeedbackButtons setDisplayFlag={setDisplayFlag} setFeedbackModal={setFeedbackModal} />
				</AIAnswerFeedbackWrapper>
			)}
			<AIAnswerContentWarningAndBrandSection />
			{(renderOpenChatButton || followUps.length > 0) && (
				<SmartAnswerFooterSection showOpenChat={renderOpenChatButton} />
			)}
			{showFeedbackModal ? (
				<Suspense fallback={<></>}>
					{sourceProduct === 'sac' ? (
						<AkFeedbackCollector
							locale={locale}
							onClose={() => setFeedbackModal(false)}
							onSubmit={() => {
								setDisplayFlag(true);
							}}
							customContent={
								<Stack space="space.050">
									{sourceProduct !== 'sac' && (
										<Checkbox
											label={formatMessage(
												messages.ai_answer_component_report_consent_checkbox_label,
											)}
											onChange={toggleConsentCheckbox}
											isChecked={hasConsent}
										/>
									)}
									{topicsEnabled && (
										<Checkbox
											label={formatMessage(
												messages.ai_answer_component_topics_not_relevant_checkbox_label,
											)}
											onChange={toggleTopicsNotRelevantCheckbox}
											isChecked={topicsNotRelevant}
										/>
									)}
								</Stack>
							}
							atlassianAccountId={id}
							name={name}
							entrypointId={FEEDBACK_ENTRYPOINT_ID_SAIN}
							additionalFields={[
								{
									id: FEEDBACK_CONTEXT_CF,
									value: feedbackContext,
								},
							]}
							feedbackTitle={formatMessage(messages.ai_answer_component_report_modal_title, {
								brandName,
							})}
							feedbackTitleDetails={
								<AIAnswerReportDescriptionWrapper>
									{formatMessage(messages.ai_answer_component_report_summary_label)}
								</AIAnswerReportDescriptionWrapper>
							}
							customFeedbackOptions={SAC_CUSTOM_FEEDBACK_OPTIONS}
							showDefaultTextFields={sourceProduct !== 'sac'}
							descriptionDefaultValue={sourceProduct === 'sac' ? 'sac feedback' : ''}
							summaryPlaceholder={formatMessage(
								messages.ai_answer_component_summary_placeholder_label,
							)}
						/>
					) : (
						<AkFeedbackCollector
							locale={locale}
							onClose={() => setFeedbackModal(false)}
							onSubmit={() => {
								setDisplayFlag(true);
							}}
							customContent={
								<Stack space="space.050">
									{sourceProduct !== 'sac' && (
										<Checkbox
											label={formatMessage(
												messages.ai_answer_component_report_consent_checkbox_label,
											)}
											onChange={toggleConsentCheckbox}
											isChecked={hasConsent}
										/>
									)}
									{topicsEnabled && (
										<Checkbox
											label={formatMessage(
												messages.ai_answer_component_topics_not_relevant_checkbox_label,
											)}
											onChange={toggleTopicsNotRelevantCheckbox}
											isChecked={topicsNotRelevant}
										/>
									)}
								</Stack>
							}
							atlassianAccountId={id}
							name={name}
							entrypointId={FEEDBACK_ENTRYPOINT_ID_SAIN}
							additionalFields={[
								{
									id: FEEDBACK_CONTEXT_CF,
									value: feedbackContext,
								},
							]}
							feedbackTitle={formatMessage(messages.ai_answer_component_report_modal_title, {
								brandName,
							})}
							feedbackTitleDetails={
								<AIAnswerReportDescriptionWrapper>
									{formatMessage(messages.ai_answer_component_report_summary_label)}
								</AIAnswerReportDescriptionWrapper>
							}
							feedbackGroupLabels={GENERIC_FEEDBACK_GROUPS_LABELS}
							summaryPlaceholder={formatMessage(
								messages.ai_answer_component_summary_placeholder_label,
							)}
						/>
					)}
				</Suspense>
			) : null}
			{displayFlag && (
				<FlagGroup onDismissed={() => setDisplayFlag(false)}>
					<Suspense fallback={<></>}>
						<AkFeedbackFlag
							locale={locale}
							description={formatMessage(messages.ai_answer_component_feedback_flag_description)}
						/>
					</Suspense>
				</FlagGroup>
			)}
		</Fragment>
	);
};

export const SearchPageSmartAnswerFooter = (): React.JSX.Element => {
	return <AIAnswerBaseFooter showOpenChat />;
};

const AIAnswerDialogFooter = () => {
	return <AIAnswerBaseFooter showWhoAnswer />;
};

export const AIAnswerFooter = (): React.JSX.Element => {
	const { isReadingAids, cardTemplate } = useAIAnswerContext();
	switch (cardTemplate) {
		case CardTemplate.SMART_ANSWER:
			return <SearchPageSmartAnswerFooter />;
		default:
			return isReadingAids ? <DefinitionFooter /> : <AIAnswerDialogFooter />;
	}
};
